import { logger } from '@/utils/logger';
import { CategoryPicker } from '@/components/categories/CategoryPicker';
import {
  CreateRuleDialog,
  RulePresets,
} from '@/components/rules/dialogs/CreateRuleDialog';
import { AILoadingAnimation } from '@/components/rules/components/AILoadingAnimation';
import { MatchTypeRadioItem } from '@/components/rules/components/MatchTypeRadioItem';
import MatchingTransactionsCountCard from '@/components/rules/components/MatchingTransactionsCountCard';
import MatchingTransactionsDialog from '@/components/rules/dialogs/MatchingTransactionsDialog';
import useImperativeDialogRef from '@/components/shared/dialogs/useImperativeDialogRef';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAi } from '@/hooks/use-ai';
import { useSnapshot } from 'valtio';
import { aiUpgradeProxy } from '@/state/ai-upgrade-state';
import { useBackBlocker } from '@/hooks/use-back-blocker';
import useCategories from '@/hooks/use-categories';
import useDebounced from '@/hooks/use-debounced';
import { useHumanReadableFormatter } from '@/hooks/use-human-readable-formatter';
import { useHumanReadableGenerator } from '@/hooks/use-human-readable-generator';
import { useInfiniteTransactions } from '@/hooks/use-infinite-transactions';
import useRules from '@/hooks/use-rules';
import { useTypingEffect } from '@/hooks/use-typing-effect';
import appServices from '@/services/appServices';
import { ActionType, AppCategory, ID } from '@polybank/shared';
import {
  rawRulesUtils,
  type KeywordMatchType,
} from '@polybank/shared/rule-utils';
import { useMutation } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  Edit2,
  List,
  Loader2,
  Pencil,
  Sparkles,
  Tag,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface AIAutomationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionDescription: string;
  transactionAmount: number;
  transactionCategory: string | undefined;
  transactionId: string;
  forceApply: boolean;
  /** Optional target category name to pre-fill and pass to AI for rule generation */
  targetCategoryName?: string;
}

export function AIAutomationDialog({
  open,
  onOpenChange,
  transactionDescription,
  transactionAmount,
  transactionCategory,
  transactionId,
  targetCategoryName,
}: AIAutomationDialogProps) {
  const { t } = useTranslation();
  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [customCategory, setCustomCategory] = useState<AppCategory>();
  const [customKeyword, setCustomKeyword] = useState<string>('');
  const [isEditingKeyword, setIsEditingKeyword] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [customMatchType, setCustomMatchType] = useState<KeywordMatchType>();

  const { createNewRule, isSavingRule } = useRules();
  const { updateItem: updateTransaction } = useInfiniteTransactions();
  const { open: upgradeModalOpen } = useSnapshot(aiUpgradeProxy);

  // Close this dialog when the upgrade modal opens
  useEffect(() => {
    if (upgradeModalOpen) {
      onOpenChange(false);
    }
  }, [upgradeModalOpen, onOpenChange]);

  const { addItem: addCategory } = useCategories();
  const {
    isLoading,
    isRegenerating,
    generateRule,
    aiSelectedCategory,
    aiSelectedKeyword,
    aiPatternType,
    actions,
    aiResponse,
    reasoning: aiReasoning,
  } = useAi();

  // Use the client-side human readable generator
  const { generate: generateHumanReadable } = useHumanReadableGenerator();

  const aiMatchType = aiPatternType || 'exact';
  const choosenMatchType = customMatchType || aiMatchType;
  const {
    ref: matchingTransactionsModalRef,
    open: openMatchingTransactionsModal,
  } = useImperativeDialogRef();

  const handleViewTransactions = useCallback(() => {
    openMatchingTransactionsModal();
  }, [openMatchingTransactionsModal]);

  useBackBlocker({
    enabled: !!aiResponse,
    onBack: () => {
      onOpenChange(false);
    },
    askConfirm: true,
    confirmTitle: t('aiAutomation.confirmLeaveTitle'),
    confirmDescription: t('aiAutomation.confirmLeaveDescription'),
  });

  useEffect(() => {
    if (open) {
      generateRule({
        transactionDescription,
        transactionAmount,
        categoryName: targetCategoryName,
      });
    }
  }, [
    open,
    transactionDescription,
    transactionAmount,
    targetCategoryName,
    generateRule,
  ]);

  const finalCategory = useMemo(() => {
    return customCategory ?? aiSelectedCategory;
  }, [customCategory, aiSelectedCategory]);

  const finalKeyword = useMemo(() => {
    return isEditingKeyword ? customKeyword : aiSelectedKeyword;
  }, [customKeyword, aiSelectedKeyword, isEditingKeyword]);

  // Build the customized rule with any user modifications
  const _rawRuleForCount = useMemo(() => {
    if (!aiResponse) return undefined;
    let result = { ...aiResponse };
    if (finalKeyword) {
      result = rawRulesUtils.changeKeyword(
        result,
        finalKeyword,
        choosenMatchType
      );
    }
    if (finalCategory?.$id) {
      result = rawRulesUtils.changeAppliedCategory(result, finalCategory?.$id);
    }
    return result;
  }, [aiResponse, finalCategory, finalKeyword, choosenMatchType]);

  const [rawRuleForCount] = useDebounced(_rawRuleForCount, 500);

  // Generate human readable from the customized rule (client-side)
  const humanReadable = useMemo(() => {
    if (!rawRuleForCount?.conditions) return '';
    return generateHumanReadable(rawRuleForCount.conditions);
  }, [rawRuleForCount?.conditions, generateHumanReadable]);

  // Typing animation hook
  const { displayedText } = useTypingEffect({
    text: humanReadable || '',
    speed: 30,
    enabled: !!humanReadable && !isRegenerating,
  });

  // Use the human readable formatter hook
  const coloredText = useHumanReadableFormatter(displayedText);

  // Handle category change from picker
  const handleCategoryChange = useCallback((category?: AppCategory) => {
    setCustomCategory(category);
  }, []);

  const onCustomKeywordChange = useCallback((keyword: string) => {
    setCustomKeyword(keyword);
  }, []);

  // Convert AI response to manual rule presets
  const manualRulePresets = useMemo((): RulePresets | undefined => {
    if (!aiResponse) return undefined;
    const toEdit = rawRulesUtils.toStructured(aiResponse);
    toEdit!.actions = toEdit!.actions?.map((action) => {
      if (action.actionType === ActionType.SET_CATEGORY) {
        return {
          ...action,
          actionValue: finalCategory?.$id,
        };
      }
      return action;
    });
    return {
      ...toEdit,
      id: 'ai-generated-rule', // ID to trigger form reset in CreateRuleDialog
      title: 'AI Automation Rule',
      description: humanReadable,
    };
  }, [aiResponse, finalCategory?.$id, humanReadable]);

  const saveCategoryMutation = useMutation({
    mutationFn: async (category: AppCategory) => {
      return appServices.categories.upsertCategory(category);
    },
  });

  // Handle opening manual edit
  const handleOpenManualEdit = useCallback(async () => {
    if (finalCategory && !finalCategory?.$id) {
      const category = await saveCategoryMutation.mutateAsync(finalCategory);
      addCategory(category);
      setCustomCategory(category);
    }
    setManualEditOpen(true);
  }, [addCategory, finalCategory, saveCategoryMutation]);

  // Handle manual edit dialog close
  const handleManualEditClose = useCallback(
    (open: boolean) => {
      setManualEditOpen(open);
      if (!open) {
        // Close the AI dialog as well when manual edit is closed (rule was created)
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  // Handle save rule with memoization
  const handleSaveRule = useCallback(async () => {
    try {
      if (finalCategory && finalKeyword) {
        const isNewCategory = !finalCategory.$id;
        if (isNewCategory) {
          finalCategory.$id = ID.unique();
        }
        let rawRule = rawRulesUtils.changeAppliedCategory(
          aiResponse!,
          finalCategory!.$id!
        );
        rawRule = rawRulesUtils.changeKeyword(
          rawRule,
          finalKeyword!,
          choosenMatchType
        );

        // // If user made customizations, regenerate human readable before saving
        const finalHumanReadable = humanReadable;

        const newRule = {
          title: 'AI Automation Rule',
          description: finalHumanReadable,
          raw: JSON.stringify(rawRule),
          active: true,
          humanReadable: finalHumanReadable,
        };

        await createNewRule({
          rule: newRule,
          newCategory: finalCategory,
          applyNow: true,
          forceApply: true,
          regenerateHumanReadable: false, // humanReadable is now generated client-side
        });

        updateTransaction({
          $id: transactionId,
          category_id: finalCategory!.$id!,
        });

        onOpenChange(false);
        toast.success(t('aiAutomation.ruleCreatedSuccess'));
      } else {
        toast.error(t('aiAutomation.selectCategoryError'));
      }
    } catch (err) {
      logger.error('Failed to save rule:', err);
      toast.error(t('aiAutomation.saveRuleError'));
    }
  }, [
    finalCategory,
    finalKeyword,
    aiResponse,
    choosenMatchType,
    humanReadable,
    createNewRule,
    updateTransaction,
    transactionId,
    onOpenChange,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <div className='w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center'>
              <Sparkles className='w-5 h-5 text-white' />
            </div>
            {t('aiAutomation.title')}
          </DialogTitle>
          <DialogDescription>{t('aiAutomation.description')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Transaction Context */}
          <div className='p-4 bg-muted/50 rounded-lg border space-y-2'>
            <div className='text-sm text-muted-foreground mb-1'>
              {t('aiAutomation.analyzingTransaction')}
            </div>
            <div className='font-medium'>{transactionDescription}</div>
            <div className='text-sm text-muted-foreground'>
              {t('aiAutomation.amount')}: ${transactionAmount} •{' '}
              {t('aiAutomation.category')}: {transactionCategory}
            </div>
          </div>

          {/* AI Loading Animation */}
          {isLoading && <AILoadingAnimation />}

          {/* Regenerating State */}
          {isRegenerating && (
            <div className='flex items-center justify-center py-8 space-x-3'>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className='w-5 h-5 text-purple-500' />
              </motion.div>
              <div className='text-sm text-muted-foreground'>
                {t('aiAutomation.customizing')}
              </div>
            </div>
          )}

          {/* AI Response */}
          {!isLoading && !isRegenerating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className='space-y-4'
            >
              {/* Human Readable Rule */}
              <div className='p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-lg border-2 border-purple-200 dark:border-purple-800'>
                <div className='flex items-start gap-3 mb-3'>
                  <div className='w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0'>
                    <CheckCircle2 className='w-5 h-5 text-white' />
                  </div>
                  <div>
                    <div className='font-semibold mb-1'>
                      {t('aiAutomation.automationRule')}
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      {t('aiAutomation.confidence', {
                        confidence: actions?.[0]?.confidence,
                      })}
                    </div>
                  </div>
                </div>

                <div className='text-base leading-relaxed'>{coloredText}</div>
                {/* Legend */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className='flex items-center gap-4 text-sm mt-4'
                >
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 bg-blue-600 rounded-full' />
                    <span className='text-muted-foreground'>
                      {t('aiAutomation.conditions')}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='w-3 h-3 bg-green-500 rounded-full' />
                    <span className='text-muted-foreground'>
                      {t('aiAutomation.actions')}
                    </span>
                  </div>
                </motion.div>
              </div>

              {/* AI Reasoning */}
              {aiReasoning && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className='p-4 bg-muted/50 rounded-lg'
                >
                  <div className='text-sm font-medium mb-1'>
                    {t('aiAutomation.aiReasoning')}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    {aiReasoning}
                  </div>
                </motion.div>
              )}

              {/* Matching Transactions Count */}
              {rawRuleForCount && (
                <MatchingTransactionsCountCard rule={rawRuleForCount} />
              )}

              {/* Rule Customization Box */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className='space-y-6 bg-muted/30 rounded-xl p-6'
              >
                {/* Customize Rule Header */}
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <Edit2 className='w-4 h-4 text-muted-foreground' />
                    <span className='font-semibold'>
                      {t('aiAutomation.customizeRule')}
                    </span>
                  </div>
                  <p className='text-sm text-muted-foreground pl-6'>
                    {t('aiAutomation.customizeHintText')}
                  </p>
                </div>

                {/* Match Keyword */}
                <div className='space-y-3'>
                  <Label className='font-medium'>
                    {t('aiAutomation.matchKeyword')}
                  </Label>
                  {isEditingKeyword ? (
                    <div className='flex items-center gap-2'>
                      <Input
                        value={customKeyword}
                        onChange={(e) => onCustomKeywordChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setIsEditingKeyword(false);
                          }
                          if (e.key === 'Escape') {
                            setCustomKeyword('');
                            setIsEditingKeyword(false);
                          }
                        }}
                        autoFocus
                        className='flex-1'
                      />
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => {
                          setCustomKeyword('');
                          setIsEditingKeyword(false);
                        }}
                      >
                        {t('aiAutomation.cancel')}
                      </Button>
                      <Button
                        size='sm'
                        onClick={() => setIsEditingKeyword(false)}
                      >
                        {t('aiAutomation.done')}
                      </Button>
                    </div>
                  ) : (
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2.5 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800'>
                        <span className='font-bold text-xl text-blue-700 dark:text-blue-400'>
                          {aiSelectedKeyword}
                        </span>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => {
                            setCustomKeyword(aiSelectedKeyword || '');
                            setIsEditingKeyword(true);
                          }}
                          className='gap-1.5 text-muted-foreground hover:text-foreground h-8'
                        >
                          <Edit2 className='w-3.5 h-3.5' />
                          {t('aiAutomation.edit')}
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className='text-sm text-muted-foreground'>
                    {t('aiAutomation.keywordAutoDescription')}
                  </p>
                </div>

                {/* Match Type Options */}
                <div className='space-y-3'>
                  {/* Exact Match */}
                  <MatchTypeRadioItem
                    matchType='exact'
                    icon={Check}
                    originalRule={rawRuleForCount}
                    choosenKeyword={finalKeyword}
                    label={t('aiAutomation.exactMatch')}
                    description={t('aiAutomation.exactMatchDescription')}
                    isSelected={choosenMatchType === 'exact'}
                    onClick={() => setCustomMatchType('exact')}
                    onViewTransactions={handleViewTransactions}
                  />
                  {/* Smart Match */}
                  <MatchTypeRadioItem
                    matchType='smart'
                    icon={Zap}
                    originalRule={rawRuleForCount}
                    choosenKeyword={finalKeyword}
                    label={t('aiAutomation.smartMatch')}
                    description={t('aiAutomation.smartMatchDescription')}
                    isSelected={choosenMatchType === 'smart'}
                    onClick={() => setCustomMatchType('smart')}
                    onViewTransactions={handleViewTransactions}
                  />
                  {/* Any of the Words */}
                  <MatchTypeRadioItem
                    matchType='any'
                    icon={List}
                    originalRule={rawRuleForCount}
                    choosenKeyword={finalKeyword}
                    label={t('aiAutomation.anyMatch')}
                    description={t('aiAutomation.anyMatchDescription')}
                    isSelected={choosenMatchType === 'any'}
                    onClick={() => setCustomMatchType('any')}
                    onViewTransactions={handleViewTransactions}
                  />
                </div>

                <div className='h-px bg-border' />

                {/* Assign Category */}
                <div className='space-y-3'>
                  <Label className='font-medium'>
                    {t('aiAutomation.assignCategory')}
                  </Label>

                  {isEditingCategory ? (
                    <div className='space-y-2'>
                      <CategoryPicker
                        currentCategoryId={finalCategory?.$id}
                        onCategoryChange={handleCategoryChange}
                      />
                    </div>
                  ) : (
                    <div className='flex items-center justify-between'>
                      <div
                        className='flex items-center gap-2.5 px-4 py-3 rounded-lg'
                        style={{ backgroundColor: finalCategory?.color }}
                      >
                        <Tag
                          className='h-3 w-3'
                          style={{ color: finalCategory?.text_color }}
                        />
                        <span
                          className='font-semibold text-lg'
                          style={{ color: finalCategory?.text_color }}
                        >
                          {finalCategory?.name}
                        </span>
                      </div>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => setIsEditingCategory(true)}
                        className='gap-1.5 text-muted-foreground hover:text-foreground h-8'
                      >
                        <Edit2 className='w-3.5 h-3.5' />
                        {t('aiAutomation.edit')}
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Action Buttons */}
          {!isLoading && !isRegenerating && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className='flex flex-wrap justify-between items-center gap-3 pt-4 border-t'
            >
              <Button
                variant='outline'
                onClick={handleOpenManualEdit}
                className='gap-2'
              >
                {saveCategoryMutation.isPending ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Pencil className='w-4 h-4' />
                )}
                {t('aiAutomation.advancedEditor')}
              </Button>
              <div className='flex gap-3'>
                <Button variant='outline' onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSaveRule}
                  disabled={isSavingRule}
                  className='gap-2'
                >
                  {isSavingRule ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <CheckCircle2 className='w-4 h-4' />
                  )}
                  {t('aiAutomation.saveRule')}
                </Button>
              </div>
            </motion.div>
          )}

          {!!rawRuleForCount && (
            <MatchingTransactionsDialog
              ref={matchingTransactionsModalRef}
              rawRule={rawRuleForCount}
            />
          )}
          {/* Manual Edit Dialog */}
          <CreateRuleDialog
            open={manualEditOpen}
            onOpenChange={handleManualEditClose}
            presets={manualRulePresets}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
